import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ProductContext } from "../../../store";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import WritingAssistantWizardCard from "../../../components/AiVisibility/WritingAssistance/WritingAssistantWizardCard";
import YourJobsCard from "../../../components/AiVisibility/WritingAssistance/YourJobsCard";
import {
	findOnlySelectableWritingRecommendation,
	findPageByUrl,
	getPageJobId,
	normalizePageUrl,
	recommendationIdsMatch,
} from "../../../components/AiVisibility/WritingAssistance/writingAssistantUtils";
import { listAiVisibilityAudits } from "../../../services/aiVisibilityApi";
import { AI_VISIBILITY_LIST_PAGE_SIZE } from "../../../components/AiVisibility/aiVisibilityListConstants";
import {
	getWritingAssistantRecommendations,
	listWritingAssistantJobs,
	submitWritingAssistantJob,
} from "../../../services/writingAssistantApi";

const AiVisibilityWritingAssistance = () => {
	const navigate = useNavigate();
	const { currentWebsiteId, userLoader, websiteLoader } = useContext(ProductContext);

	const [audits, setAudits] = useState([]);
	const [recommendations, setRecommendations] = useState(null);
	const [jobs, setJobs] = useState([]);
	const [jobsTotal, setJobsTotal] = useState(0);
	const [jobsHasMore, setJobsHasMore] = useState(false);
	const [jobsPage, setJobsPage] = useState(0);

	const [auditId, setAuditId] = useState(null);
	const [selectedRecommendationId, setSelectedRecommendationId] = useState(null);
	const [selectedPageUrl, setSelectedPageUrl] = useState(null);
	const [activeJobId, setActiveJobId] = useState(null);

	const [loadingAudits, setLoadingAudits] = useState(true);
	const [loadingRecs, setLoadingRecs] = useState(false);
	const [loadingJobs, setLoadingJobs] = useState(true);
	const [submitting, setSubmitting] = useState(false);

	const fetchAudits = useCallback(async () => {
		if (!currentWebsiteId) {
			setAudits([]);
			setLoadingAudits(false);
			return;
		}
		setLoadingAudits(true);
		const result = await listAiVisibilityAudits(currentWebsiteId, 0, 20);
		if (!result.ok) {
			toast.error(result.message);
			setAudits([]);
		} else {
			const completed = (result.audits || []).filter(
				(a) => String(a.status).toLowerCase() === "completed"
			);
			setAudits(completed);
		}
		setLoadingAudits(false);
	}, [currentWebsiteId]);

	const fetchJobs = useCallback(async () => {
		if (!currentWebsiteId) {
			setJobs([]);
			setJobsTotal(0);
			setJobsHasMore(false);
			setLoadingJobs(false);
			return;
		}
		setLoadingJobs(true);
		const result = await listWritingAssistantJobs(currentWebsiteId, {
			skip: jobsPage * AI_VISIBILITY_LIST_PAGE_SIZE,
			limit: AI_VISIBILITY_LIST_PAGE_SIZE,
		});
		if (!result.ok) {
			toast.error(result.message);
			setJobs([]);
			setJobsTotal(0);
			setJobsHasMore(false);
		} else {
			setJobs(result.jobs || []);
			setJobsTotal(result.total || 0);
			setJobsHasMore(result.hasMore ?? false);
		}
		setLoadingJobs(false);
	}, [currentWebsiteId, jobsPage]);

	useEffect(() => {
		setJobsPage(0);
	}, [currentWebsiteId]);

	const fetchRecommendations = useCallback(async () => {
		if (!currentWebsiteId || auditId == null) {
			setRecommendations(null);
			return;
		}
		setLoadingRecs(true);
		const result = await getWritingAssistantRecommendations(currentWebsiteId, auditId);
		if (!result.ok) {
			toast.error(result.message);
			setRecommendations([]);
		} else {
			setRecommendations(result.recommendations || []);
		}
		setLoadingRecs(false);
	}, [currentWebsiteId, auditId]);

	useEffect(() => {
		fetchAudits();
	}, [fetchAudits]);

	useEffect(() => {
		fetchJobs();
	}, [fetchJobs]);

	useEffect(() => {
		fetchRecommendations();
	}, [fetchRecommendations]);

	useEffect(() => {
		if (loadingRecs || !recommendations?.length || selectedRecommendationId != null) {
			return;
		}

		const onlyActive = findOnlySelectableWritingRecommendation(recommendations);
		if (!onlyActive) return;

		setSelectedRecommendationId(onlyActive.id);
		const firstPage = onlyActive.affected_pages?.[0];
		if (firstPage?.url) {
			setSelectedPageUrl(firstPage.url);
			setActiveJobId(getPageJobId(firstPage));
		}
	}, [loadingRecs, recommendations, selectedRecommendationId]);

	const selectedRec = useMemo(
		() =>
			recommendations?.find((r) => recommendationIdsMatch(r.id, selectedRecommendationId)) ??
			null,
		[recommendations, selectedRecommendationId]
	);

	const selectedPage = useMemo(
		() => findPageByUrl(selectedRec?.affected_pages, selectedPageUrl),
		[selectedRec, selectedPageUrl]
	);

	const handleAuditSelect = useCallback((nextAuditId) => {
		setAuditId(nextAuditId);
		setSelectedRecommendationId(null);
		setSelectedPageUrl(null);
		setActiveJobId(null);
		setRecommendations(null);
	}, []);

	const handleRecommendationSelect = useCallback((recId) => {
		setSelectedRecommendationId(recId);
		setSelectedPageUrl(null);
		setActiveJobId(null);
	}, []);

	const handlePageSelect = useCallback(
		(url) => {
			setSelectedPageUrl(url);
			const rec = recommendations?.find((r) =>
				recommendationIdsMatch(r.id, selectedRecommendationId)
			);
			if (rec && url) {
				const page = findPageByUrl(rec.affected_pages, url);
				setActiveJobId(getPageJobId(page));
			} else {
				setActiveJobId(null);
			}
		},
		[recommendations, selectedRecommendationId]
	);

	const handleGeneratePreview = useCallback(async () => {
		if (
			!currentWebsiteId ||
			auditId == null ||
			selectedRecommendationId == null ||
			!selectedPageUrl
		) {
			return;
		}

		const rec = recommendations?.find((r) =>
			recommendationIdsMatch(r.id, selectedRecommendationId)
		);
		if (rec?.processed) return;

		setSubmitting(true);

		try {
			const submit = await submitWritingAssistantJob(currentWebsiteId, {
				auditId,
				recommendationId: selectedRecommendationId,
				url: normalizePageUrl(selectedPageUrl),
			});

			if (!submit.ok || !submit.job?.id) {
				toast.error(submit.message || "Failed to submit job");
				return;
			}

			if (jobsPage === 0) {
				await fetchJobs();
			} else {
				setJobsPage(0);
			}
			navigate(
				`/aivisibility/writing-assistance/view?jobId=${encodeURIComponent(submit.job.id)}`
			);
		} catch {
			toast.error("Failed to generate preview");
		} finally {
			setSubmitting(false);
		}
	}, [
		currentWebsiteId,
		auditId,
		selectedRecommendationId,
		selectedPageUrl,
		recommendations,
		fetchJobs,
		jobsPage,
		navigate,
	]);

	if (userLoader || websiteLoader) {
		return <DetailSkeleton />;
	}

	if (!currentWebsiteId) {
		return null;
	}

	return (
		<div className="contentBlock ai-visibility-dashboard writing-assistance">
			<WritingAssistantWizardCard
				audits={audits}
				loadingAudits={loadingAudits}
				recommendations={recommendations}
				loadingRecs={loadingRecs}
				auditId={auditId}
				selectedRecommendationId={selectedRecommendationId}
				selectedPageUrl={selectedPageUrl}
				selectedRec={selectedRec}
				selectedPage={selectedPage}
				activeJobId={activeJobId}
				canGenerate={!submitting}
				onAuditSelect={handleAuditSelect}
				onRecommendationSelect={handleRecommendationSelect}
				onPageSelect={handlePageSelect}
				onGeneratePreview={handleGeneratePreview}
			/>
			<YourJobsCard
				jobs={jobs}
				loading={loadingJobs}
				total={jobsTotal}
				page={jobsPage}
				pageSize={AI_VISIBILITY_LIST_PAGE_SIZE}
				hasMore={jobsHasMore}
				onPageChange={setJobsPage}
			/>
		</div>
	);
};

export default AiVisibilityWritingAssistance;

